/**
 * Client-side helper that POSTs panel state to the dev-only save-defaults
 * middleware. Returns a structured result so callers can show success/error
 * status to the user. In production builds (`import.meta.env.DEV === false`)
 * this resolves to a no-op error so the call site is identical in both modes.
 */
export interface SaveDefaultsResult {
  ok: boolean;
  error?: string;
  bytes?: number;
}

export type DefaultsFilePath =
  | 'src/render/StyleConfig.defaults.json'
  | 'src/game/Camera.defaults.json'
  | 'src/game/Handling.defaults.json'
  | 'src/game/PodRacerRig.defaults.json'
  | 'src/audio/Audio.defaults.json'
  | 'src/render/presets/bindings.json'
  | `src/scene/assemblies/${string}.json`
  | `src/scene/templates/${string}/${string}.json`
  // `src/render/presets/<name>.json` — user-named map style presets.
  | `src/render/presets/${string}.json`;

export async function saveDefaultsToSource(
  file: DefaultsFilePath,
  json: unknown,
): Promise<SaveDefaultsResult> {
  if (!import.meta.env.DEV) {
    return { ok: false, error: 'save-to-source is dev-only' };
  }

  try {
    const res = await fetch('/__dev/save-defaults', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file, json }),
    });
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, error: `HTTP ${res.status}: ${text}` };
    }
    const data = (await res.json()) as { ok?: boolean; bytes?: number };
    return { ok: !!data.ok, bytes: data.bytes };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export async function bakeStylePresetAssets(
  presetName: string,
  trackUrl: string,
  style: unknown,
): Promise<SaveDefaultsResult> {
  if (!import.meta.env.DEV) {
    return { ok: false, error: 'style-asset-bake is dev-only' };
  }

  try {
    const res = await fetch('/__dev/bake-style-assets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ presetName, trackUrl, style }),
    });
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, error: `HTTP ${res.status}: ${text}` };
    }
    const data = (await res.json()) as { ok?: boolean; bytes?: number };
    return { ok: !!data.ok, bytes: data.bytes };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export function isSaveToSourceAvailable(): boolean {
  return import.meta.env.DEV;
}
